import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useChildAttention } from '../../hooks/useChildAttention';
import { useSyncParentContext } from '../../hooks/useSyncParentContext';
import ChildPickerSheet, { Avatar } from './ChildPickerSheet';

/**
 * The name at the top: the only control that selects the active child.
 * One child → a plain chip. Several → a button opening the picker sheet;
 * its badge counts what is waiting on the OTHER children, so a parent on
 * Zara still sees that Aoife needs them.
 */
const ChildSwitcher: React.FC = () => {
  const { activeStudent, children, isLoading, homeChildId, goToStudent } = useActiveStudent();
  const { byChild, totalExcluding } = useChildAttention();
  const sync = useSyncParentContext();
  const [open, setOpen] = useState(false);

  if (isLoading) return <div className="h-9 w-32 rounded-full bg-gray-100 animate-pulse" />;
  if (children.length === 0) return null;

  if (children.length === 1) {
    const only = children[0];
    return (
      <button type="button" data-testid="kid-picker-single" onClick={() => goToStudent(only.id, '')} className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-gray-100 text-left">
        <Avatar name={only.displayName} size="sm" />
        <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{only.displayName}</span>
      </button>
    );
  }

  // On /parent/account/* and /settings/* no child is in the URL; show the
  // remembered child rather than "Pick a child", and exclude it from the badge.
  const shown = activeStudent ?? children.find((c) => c.id === homeChildId) ?? null;
  const others = totalExcluding(shown?.id);
  const pick = async (child: { id: string; locationId: string; organizationId: string }) => {
    setOpen(false);
    await sync(child.locationId, child.organizationId);
    goToStudent(child.id);
  };
  return (
    <>
      <button type="button" data-testid="kid-picker-button" onClick={() => setOpen(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500">
        {shown ? <Avatar name={shown.displayName} size="sm" /> : null}
        <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{shown?.displayName ?? 'Pick a child'}</span>
        {others > 0 && (
          <span data-testid="kid-picker-total-badge" aria-label={`${others} thing${others === 1 ? '' : 's'} waiting for your other children`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1">{others}</span>
        )}
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </button>
      <ChildPickerSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Switch child"
        options={children}
        selectedId={shown?.id}
        attention={byChild}
        showLocation={new Set(children.map((c) => c.locationId)).size > 1}
        onPick={(c) => pick(children.find((k) => k.id === c.id)!)}
        testIdPrefix="kid-picker-child"
      />
    </>
  );
};
export default ChildSwitcher;
